/**
 * ── ONE YIELD, AND ONE BUDGET, FOR EVERY LOAD-TIME WALK THAT WOULD OTHERWISE HOLD THE THREAD ─────
 *
 * This module exists because there are now TWO of those walks and they had one implementation
 * between them plus one that did not exist at all:
 *
 *   · `entities/staticMesh.extractCollision` — the per-ASSET trimesh walk (177 of them, cached).
 *     Budgeted since L1.0, and the numbers in {@link YIELD_BUDGET_MS} are its measurement.
 *   · `app/mount.bindCollision` — the per-PLACEMENT bind (5,563 of them on `mars-hangar-01`).
 *     Unbudgeted, unyielding, and the reason that level rendered at ~0.05 fps while it loaded.
 *
 * It is not a utility grab-bag: a second copy of the rAF/timer race below would be a second answer
 * to *"how does a long build let a frame happen"*, and the failure it guards is invisible from a
 * diff (a world that never finishes loading in a background tab).
 *
 * ⚠️ PLACED IN `src/world/` DELIBERATELY. `src/entities/**` may not import `src/app/**`, and
 * `src/app/**` should not reach into an entity module — `src/world/` is the one layer both already
 * import (`readiness.ts`, `collision.ts`, `assets.ts`). It is NOT one of `purity.test.ts`'s pure
 * files and must never be added to that list: it touches `requestAnimationFrame` and `setTimeout` by
 * definition, which is the whole point of it.
 */

/**
 * One frame of breathing room — rAF where there is a frame, else a tick.
 *
 * ⚠️ IT RACES A TIMER, AND WITHOUT THAT IT NEVER COMPLETES IN A BACKGROUND TAB.
 * `requestAnimationFrame` does not fire while `document.hidden`, so a world opened in a tab that is
 * not compositing left `inst.ready` pending forever: nothing bound, `state().collision` empty, and
 * since S7 the world-readiness gate would wait on a promise that can never settle. rAF is still
 * preferred when it fires — it lands the work between paints — and the timer is the floor that
 * guarantees progress either way.
 */
export const yieldFrame = (): Promise<void> =>
  new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      resolve();
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(finish);
    setTimeout(finish, 32);
  });

/**
 * ── ⭐ YIELD WITHOUT WAITING FOR A PAINT — AND WHY BOTH KINDS HAVE TO EXIST ─────────────────────
 *
 * {@link yieldFrame} BLOCKS UNTIL A FRAME HAPPENS. That is the right shape when the work is small
 * and you want it landed between paints, and it is a trap when the work is large and the frames are
 * slow — because the cost of a yield is then a whole load frame, not a tick.
 *
 * ⚠️ MEASURED, in a real browser on `mars-hangar-01`, and this is a mistake made ONCE IN EACH
 * DIRECTION in this repo. `staticMesh` recorded the first: yielding per primitive cost 2,715 ms of a
 * 2,923 ms extraction, all of it waiting. The bind queue then reproduced it at scale — an 8 ms
 * budget over ~36 s of collider building is ~4,500 slices, each blocking on a load frame, and the
 * measurement was unambiguous:
 *
 *     gaps > 100 ms   102 -> 23      the tail got better
 *     gaps > 1 s       12 -> 6
 *     colliders bound at 'playing'   5,563 -> 1,152     …and completion fell off a cliff
 *
 * Better tail, far worse throughput: yielding to a 50 ms frame after every 8 ms of work spends most
 * of the load asleep. So the queue yields with a MACROTASK instead. The browser is free to paint
 * between two macrotasks — that is the whole point — but the loader does not sit and wait for it.
 *
 * `setTimeout(0)` and not `queueMicrotask`: a microtask runs INSIDE the current task and yields to
 * nothing at all, which is the bug this exists to fix. Nested timers clamp to ~4 ms after five
 * levels, which is a real cost and a small one at the budgets this is used with.
 */
export const yieldTask = (): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, 0);
  });

/**
 * ⭐ ONE YIELD PER ~8 ms OF WORK, NOT ONE PER ITEM (L1.0) — 93 % OF THE WAIT, IN TEN LINES.
 *
 * The measured cost of Sponza's extraction was 2,923 ms, and the walk is only ~26 ms of it: 103
 * primitives of ~0.25 ms each. The other 2,715 ms was 102 unconditional round trips through a 26 ms
 * load frame. Yielding between EVERY pair of items bought nothing an 8 ms budget does not: the
 * anti-stall property is *"no synchronous turn longer than ~8 ms + one item"*, which the budget
 * keeps exactly, and the waiting is deleted.
 *
 * ⚠️ It is the POLICY that is tunable, not {@link yieldFrame} itself, whose rAF/timer race is
 * load-bearing (rAF does not fire in a hidden tab, and without the timer nothing ever settles).
 *
 * ⚠️ AND THE BUDGET BOUNDS THE TURN, NOT THE ITEM. One item longer than 8 ms still blocks for its
 * whole duration — the budget can only decide not to start ANOTHER one. On `mars-hangar-01` a single
 * `createCollider(trimesh)` is well under a millisecond and the property holds; a level whose one
 * asset takes 300 ms to bind would still drop a frame, and the fix for that is a smaller unit of
 * work, never a smaller budget.
 */
export const YIELD_BUDGET_MS = 8;

/**
 * ⭐ THE BUDGET FOR WORK DONE WHILE THE LOADING PLATE IS UP — AND IT IS DELIBERATELY 12× LARGER.
 *
 * The cost of a long turn is not a constant: it depends on who is looking. Behind an opaque plate
 * nobody is aiming a camera, so a 100 ms turn is ~10 fps of plate animation and the thing that
 * actually matters is finishing; once the world is interactive, a 100 ms turn is a visible lurch and
 * the thing that matters is smoothness. One number cannot be right for both, and using the small one
 * everywhere is what made collider binding fall from 5,563 bound to 1,152 in the measurement above.
 *
 * ⚠️ IT IS NOT A LICENCE TO BLOCK. Paired with {@link yieldTask} it bounds the turn at ~100 ms plus
 * one item — against the 12.5 s, 9.2 s and 8.8 s blocks this replaced, which is the whole point.
 */
export const LOADING_BUDGET_MS = 100;
